import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../../apiBase';

const REGIONS = ['CIS', 'EAST', 'EUROPE'];
const DOC_TYPES = ['PrivacyPolicy', 'TermsOfService', 'RefundPolicy'];

export default function LegalDocumentsAdminPage() {
    const { t } = useTranslation('legalDocs');
    const [list, setList] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [filterRegion, setFilterRegion] = useState('');
    const [filterType, setFilterType] = useState('');
    const [edit, setEdit] = useState({ title: '', content: '', version: '1.0', isActive: true });
    const [preview, setPreview] = useState('');
    const token = localStorage.getItem('token');
    const api = useMemo(() => axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${token}` },
    }), [token]);

    useEffect(() => { load(); }, [filterRegion, filterType]);

    async function load() {
        const params = {};
        if (filterRegion) params.region = filterRegion;
        if (filterType) params.type = filterType;
        const res = await api.get('/legal/documents/admin', { params });
        setList(res.data);
    }

    async function selectDoc(id) {
        setSelectedId(id);
        setPreview('');
        const res = await api.get(`/legal/documents/admin/${id}`);
        const d = res.data;
        setEdit({
            title: d.title || '',
            content: d.content || '',
            version: d.version || '1.0',
            isActive: d.isActive ?? true,
        });
    }

    async function save() {
        if (!selectedId) return;
        await api.put(`/legal/documents/${selectedId}`, {
            title: edit.title,
            content: edit.content,
            version: edit.version,
            isActive: edit.isActive,
        });
        await load();
    }

    async function publishVersion() {
        if (!selectedId) return;
        const res = await api.post(`/legal/documents/${selectedId}/publish`, {
            title: edit.title,
            content: edit.content,
        });
        setEdit((prev) => ({ ...prev, version: res.data.version }));
        await load();
    }

    async function showPreview() {
        if (!selectedId) return;
        const res = await api.post(`/legal/documents/${selectedId}/preview`);
        setPreview(res.data.html);
    }

    const selectedMeta = list.find((d) => d.id === selectedId);

    return (
        <OwnerPageLayout title={t('adminTitle')}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <select className="sp-input" value={filterRegion} onChange={(e) => setFilterRegion(e.target.value)}>
                    <option value="">{t('adminAllRegions', { defaultValue: 'Все регионы' })}</option>
                    {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <select className="sp-input" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                    <option value="">{t('adminAllTypes', { defaultValue: 'Все типы' })}</option>
                    {DOC_TYPES.map((dt) => <option key={dt} value={dt}>{dt}</option>)}
                </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px,1fr) minmax(320px,2fr)', gap: 20 }}>
                <div className="sp-panel" style={{ padding: 12, maxHeight: '70vh', overflow: 'auto' }}>
                    {list.map((d) => (
                        <button
                            key={d.id}
                            type="button"
                            className="sp-btn"
                            style={{
                                display: 'block', width: '100%', textAlign: 'left', marginBottom: 8,
                                background: selectedId === d.id ? 'var(--sp-primary-bg)' : '#fff',
                            }}
                            onClick={() => selectDoc(d.id)}
                        >
                            <strong>{d.documentType}</strong>
                            <div style={{ fontSize: 12, color: '#64748b' }}>
                                {d.region} / {d.languageCode} v{d.version}
                            </div>
                        </button>
                    ))}
                </div>
                <div className="sp-panel" style={{ padding: 16 }}>
                    {selectedId ? (
                        <>
                            {selectedMeta && (
                                <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                                    {selectedMeta.region} · {selectedMeta.languageCode}
                                    {selectedMeta.updatedAt && (
                                        <> · {t('updated')}: {new Date(selectedMeta.updatedAt).toLocaleString()}</>
                                    )}
                                </p>
                            )}
                            <input className="sp-input" style={{ marginBottom: 12 }} value={edit.title}
                                onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
                            <textarea
                                className="sp-input"
                                rows={16}
                                style={{ fontFamily: 'monospace', fontSize: 12, marginBottom: 12 }}
                                value={edit.content}
                                onChange={(e) => setEdit({ ...edit, content: e.target.value })}
                            />
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                                <span style={{ fontSize: 13 }}>{t('version')}: {edit.version}</span>
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <input type="checkbox" checked={edit.isActive}
                                    onChange={(e) => setEdit({ ...edit, isActive: e.target.checked })} />
                                {t('adminActive')}
                            </label>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button type="button" className="sp-btn sp-btn--primary" onClick={save}>{t('adminSave')}</button>
                                <button type="button" className="sp-btn" onClick={showPreview}>{t('adminPreview')}</button>
                                <button type="button" className="sp-btn" onClick={publishVersion}>
                                    {t('adminPublish', { defaultValue: 'Опубликовать новую версию' })}
                                </button>
                            </div>
                            {preview && (
                                <div className="sp-data-card" style={{ marginTop: 16, padding: 16 }}
                                    dangerouslySetInnerHTML={{ __html: preview }} />
                            )}
                        </>
                    ) : (
                        <p style={{ color: '#64748b' }}>{t('adminEdit')}</p>
                    )}
                </div>
            </div>
        </OwnerPageLayout>
    );
}
